import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from './ToastContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const AuthContext = createContext(null);

// Define session storage keys
const SESSION_KEYS = {
    USER_PROFILE: 'userProfile',
    USER_ID: 'userId',
    IS_AUTHENTICATED: 'isAuthenticated',
    SESSION_START: 'sessionStart'
};

// Session timeout in milliseconds (4 hours)
const SESSION_TIMEOUT = 4 * 60 * 60 * 1000;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { addToast } = useToast();

    // Check session validity
    const isSessionValid = useCallback(() => {
        const sessionStart = localStorage.getItem(SESSION_KEYS.SESSION_START);
        if (!sessionStart) return false;

        const now = new Date().getTime();
        const sessionAge = now - parseInt(sessionStart);
        return sessionAge < SESSION_TIMEOUT;
    }, []);

    // Clear all session data
    const clearSession = useCallback(() => {
        Object.values(SESSION_KEYS).forEach(key => localStorage.removeItem(key));
        setUser(null);
    }, []);

    // Initialize authentication state
    const initAuth = useCallback(async () => {
        const storedUser = localStorage.getItem(SESSION_KEYS.USER_PROFILE);
        const isAuthenticated = localStorage.getItem(SESSION_KEYS.IS_AUTHENTICATED);
        
        if (storedUser && isAuthenticated === 'true') {
            if (!isSessionValid()) {
                console.log('Session expired');
                clearSession();
                // Don't show toast or redirect from public/auth routes
                const path = window.location.pathname || '';
                const isPublicRoute = path.startsWith('/login') || 
                                     path.startsWith('/register') || 
                                     path.startsWith('/verify-email') ||
                                     path.startsWith('/resend-verification') ||
                                     path.startsWith('/forgot-password') ||
                                     path.startsWith('/reset-password');
                if (!isPublicRoute) {
                    addToast('Session expired. Please login again.', 'info');
                    navigate('/login');
                }
            } else {
                try {
                    const userData = JSON.parse(storedUser);
                    setUser(userData.data ? userData.data : userData);
                } catch (error) {
                    console.error('Error initializing auth:', error);
                    clearSession();
                }
            }
        }
        setLoading(false);
    }, [navigate, addToast, clearSession, isSessionValid]);

    useEffect(() => {
        initAuth();
    }, [initAuth]);

    // Handle login
    const handleLogin = useCallback(async (userData) => {
        console.log('\n=== Client Auth Context: Login Process ===');
        console.log('Received user data:', JSON.stringify(userData, null, 2));
        
        if (!userData) {
            console.error('Invalid user data received');
            throw new Error('Invalid user data');
        }

        try {
            // Set session start time
            const now = new Date().getTime();
            localStorage.setItem(SESSION_KEYS.SESSION_START, now.toString());

            // Update user state and localStorage
            await Promise.all([
                (async () => {
                    console.log('Setting user state...');
                    setUser(userData);
                })(),
                (async () => {
                    console.log('Updating localStorage...');
                    console.log('Updating localStorage... SESSION_KEYS.USER_PROFILE: ' + JSON.stringify({ data: userData, lastFetched: Date.now() }));
                    localStorage.setItem(SESSION_KEYS.USER_PROFILE, JSON.stringify({ data: userData, lastFetched: Date.now() }));
                    localStorage.setItem(SESSION_KEYS.USER_ID, userData.userId);
                    localStorage.setItem(SESSION_KEYS.IS_AUTHENTICATED, 'true');
                })()
            ]);

            console.log('Login process completed successfully');
            console.log('=== Client Auth Context: Login Process Completed ===\n');

            addToast('Successfully logged in', 'success');
            navigate('/dashboard', { replace: true });
        } catch (error) {
            console.error('Login error in AuthContext:', error);
            clearSession();
            throw error;
        }
    }, [navigate, addToast, clearSession]);

    // Handle logout
    const handleLogout = useCallback(async () => {
        try {
            console.log('\n=== Client Auth Context: Logout Process ===');
            clearSession();
            addToast('Successfully logged out', 'info');
            navigate('/login', { replace: true });
            console.log('=== Client Auth Context: Logout Process Completed ===\n');
        } catch (error) {
            console.error('Error during logout:', error);
            // Force clear session even if there's an error
            clearSession();
            navigate('/login', { replace: true });
        }
    }, [navigate, addToast, clearSession]);

    // Update user data
    const updateUser = useCallback((newData) => {
        setUser(prev => {
            const updated = { ...prev, ...newData };
            localStorage.setItem(SESSION_KEYS.USER_PROFILE, JSON.stringify({ data: updated, lastFetched: Date.now() }));
            return updated;
        });
    }, []);

    const value = {
        user,
        isAuthenticated: !!user && isSessionValid(),
        loading,
        login: handleLogin,
        logout: handleLogout,
        updateUser
    };

    return (
        <AuthContext.Provider value={value}>
            {loading ? (
                <LoadingSpinner fullScreen />
            ) : (
                children
            )}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 